import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Globe, Lock, Mail } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e2d19eae-21d9-4f5b-a36f-57b16cd8c13e/artifacts/91mwiq27_Aljabr-Laundry-Main-Logo-.png";

export default function LoginPage() {
  const { login } = useAuth();
  const { t, toggleLanguage, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('savedSuccess'));
      navigate('/admin');
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-4" dir={isRTL ? 'rtl' : 'ltr'} data-testid="login-page">
      <div className="w-full max-w-sm">
        {/* Language Switcher */}
        <div className="flex justify-end mb-6">
          <Button variant="ghost" size="sm" onClick={toggleLanguage} className="text-slate-500" data-testid="login-language-switcher">
            <Globe className="h-4 w-4 me-1" />
            {t('language')}
          </Button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Aljabr Laundry" className="h-14 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900">{t('loginTitle')}</h1>
            <p className="text-sm text-slate-400 mt-1">{t('loginSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-600 text-sm">{t('email')}</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@aljabr.com"
                  className="ps-10 h-11"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-600 text-sm">{t('password')}</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="********"
                  className="ps-10 h-11"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0056A6] hover:bg-[#004E93] text-white font-semibold rounded-xl shadow-lg shadow-blue-500/20"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                t('signIn')
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          {isRTL ? 'مغسلة الجبر' : 'Aljabr Laundry'} &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
